import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Microsoft } from '@mui/icons-material';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '../stores';
import loginBg from '../assets/login-bg.png';
import pathfinderLogo from '../assets/pathfinder-logo.png';

export interface LoginPageProps {
  /** Optional short heading above the message (e.g. "Session ended"). */
  heading?: string;
  /** Replaces the default "Sign in with your Microsoft account…" copy. */
  message?: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ heading, message }) => {
  const { loginWithAzure, loading } = useAuthStore(
    useShallow((s) => ({ loginWithAzure: s.loginWithAzure, loading: s.loading }))
  );

  const handleLogin = async () => {
    try {
      await loginWithAzure();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        // The background is a landscape composition with the tagline, red edge
        // bars, and ENDURION baked in at the top/bottom edges. Use `contain` so
        // none of those get cropped; the near-black letterbox blends with the
        // image edges on standard widescreen displays.
        backgroundImage: `url(${loginBg})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: '#000',
      }}
    >
      {/* Centered sign-in card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 6,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            borderRadius: 2,
            px: { xs: 3, sm: 4 },
            py: 4,
            bgcolor: 'rgba(10,10,12,0.92)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          }}
        >
          {/* Brand lockup: PATHFINDER (white emblem + wordmark) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Box
              component="img"
              src={pathfinderLogo}
              alt="Pathfinder"
              sx={{ width: 230, maxWidth: '80%', height: 'auto', display: 'block' }}
            />
          </Box>

          {heading && (
            <Typography
              sx={{
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 600,
                textAlign: 'center',
                mb: 0.75,
              }}
            >
              {heading}
            </Typography>
          )}
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.85rem',
              textAlign: 'center',
              lineHeight: 1.45,
              mb: 3,
            }}
          >
            {message ??
              'Sign in with your Microsoft account to access the Pathfinder advanced analytics platform.'}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleLogin}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Microsoft />
              )
            }
            sx={{
              py: 1.35,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#fff',
              background: 'linear-gradient(180deg, #6f1a24 0%, #4c1218 100%)',
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(180deg, #7e1d29 0%, #59141b 100%)',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                background: 'rgba(76,18,24,0.55)',
                color: 'rgba(255,255,255,0.7)',
              },
            }}
          >
            {loading ? 'Signing In…' : 'Sign in with Microsoft'}
          </Button>

          <Typography
            sx={{
              mt: 2.5,
              color: 'rgba(255,255,255,0.45)',
              fontSize: '0.72rem',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            This application uses Azure AD authentication with role-based access
            control. Contact your administrator if you need access.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
